import React, { useState, useEffect, useMemo } from 'react';
import { Card, Row, Col, Input, DatePicker, Button, Table, Divider, Space, message, Form, InputNumber, Typography, Modal, Tooltip, ConfigProvider, theme } from 'antd';
import { ToolOutlined, ExclamationCircleOutlined, FileTextOutlined, PlusOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';

import useAuthStore from '../../store/useAuthStore';
import usePermissions from '../../hooks/usePermissions';
import useDebounce from '../../hooks/useDebounce';
import { useFormNavigation } from '../../hooks/useFormNavigation'; // <-- HOOK IMPORT
import { MODULE_IDS } from '../../utils/constants';
import { formatQty, formatDate } from '../../utils/formatters';

import { getProductions, getProductionById, createProduction, deleteProduction, getConsumptionPreview } from '../../api/production.api';
import { getAllRawMaterials } from '../../api/rawMaterials.api';

import SearchableSelect from '../../components/common/SearchableSelect';
import ConfirmDelete from '../../components/common/ConfirmDelete';
import DataTable from '../../components/common/DataTable';
import StockBadge from '../../components/common/StockBadge';

const { Text } = Typography;

/* ─────────────────────────────────────────────
   Light Theme & Compact Override Styles
───────────────────────────────────────────── */
const LightThemeStyles = () => (
  <style>{`
    .light-production-page {
      --bg: #f8fafc;
      --surface: #ffffff;
      --surface-2: #f1f5f9;
      --border: #e2e8f0;
      --border-hi: #cbd5e1;
      --text-1: #0f172a;
      --text-2: #334155;
      --text-3: #64748b;
      --accent: #f59e0b;
      font-family: 'Barlow', sans-serif;
    }

    .light-production-page .ant-card {
      background: var(--surface);
      border-color: var(--border);
      border-radius: 6px;
      box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
    }
    
    .compact-section {
      background: #f8fafc;
      border: 1px solid var(--border);
      border-radius: 4px;
      padding: 8px 12px;
      margin-bottom: 12px;
    }

    .section-title {
      font-size: 10px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: var(--text-3);
      margin-bottom: 6px;
      display: flex;
      align-items: center;
      gap: 4px;
    }

    .light-production-page .ant-form-item { margin-bottom: 0 !important; }
    .light-production-page .ant-form-item-label { padding-bottom: 2px; }
    .light-production-page .ant-form-item-label > label { font-size: 11px !important; color: var(--text-2); height: 18px; font-weight: 600; }
    
    .light-production-page .ant-input-sm,
    .light-production-page .ant-input-number-sm, 
    .light-production-page .ant-select-sm .ant-select-selector,
    .light-production-page .ant-picker-sm {
      border-radius: 3px;
      font-size: 12px;
    }

    .light-production-page .ant-table-thead > tr > th {
      background: var(--surface-2) !important;
      color: var(--text-2) !important;
      font-weight: 600;
      font-size: 10px;
      text-transform: uppercase;
      padding: 4px 8px !important;
    }

    .light-production-page .ant-table-tbody > tr > td {
      padding: 4px 8px !important;
      font-size: 12px;
      color: var(--text-1);
    }

    .error-row-highlight { background-color: #fff2f0 !important; }
  `}</style>
);

const S = {
  tableWrap: { border: '1px solid var(--border)', borderRadius: '6px', overflow: 'hidden', marginBottom: 16 },
  emptyState: { textAlign: 'center', padding: '30px 20px', background: '#f8fafc', borderRadius: '6px', border: '1px dashed #cbd5e1' },
  emptyText: { color: '#64748b', fontSize: 13, marginTop: 8 }
};

const ExpandedProductionDetails = ({ plantId, pid }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['productionDetail', plantId, pid],
    queryFn: () => getProductionById(plantId, pid),
    enabled: !!pid && !!plantId,
  });

  const columns = [
    { title: 'Raw Material', dataIndex: 'rawName', key: 'rawName' },
    { title: 'Expected Qty', dataIndex: 'expectedQty', key: 'expectedQty', render: val => formatQty(val) },
    { title: 'Actual Qty', dataIndex: 'consumptionQty', key: 'consumptionQty', render: val => <b>{formatQty(val)}</b> },
    { title: 'Wastage', dataIndex: 'wastageQty', key: 'wastageQty', render: val => formatQty(val) },
    { title: 'Unit', dataIndex: 'unitDisplayName', key: 'unitDisplayName' },
  ];

  return <Table columns={columns} dataSource={data?.consumptionDetails || []} pagination={false} size="small" loading={isLoading} rowKey="rcid" style={{ margin: '8px', border: '1px solid #e2e8f0' }} />;
};

// --- MAIN COMPONENT ---
const ProductionEntryPage = () => {
  const { user } = useAuthStore();
  const plantId = user?.plantID;
  const queryClient = useQueryClient();
  const { canAdd, canDelete } = usePermissions(MODULE_IDS.PRODUCTION);

  const [form] = Form.useForm();
  const [showEntryForm, setShowEntryForm] = useState(false);

  // <-- HOOK INITIALIZATION -->
  // यह हुक सिर्फ तभी एक्टिव होगा जब showEntryForm true हो
  const formContainerRef = useFormNavigation(showEntryForm); 

  const [previewItems, setPreviewItems] = useState([]);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [historyParams, setHistoryParams] = useState({ current: 1, pageSize: 10, from: null, to: null, readyProductId: null });

  const selectedProduct = Form.useWatch('readyProductId', form);
  const productionQty = Form.useWatch('productionQty', form);
  const debouncedQty = useDebounce(productionQty, 500);

  useEffect(() => {
    if (showEntryForm) {
      form.resetFields();
      setPreviewItems([]);
    }
  }, [showEntryForm, form]);

  const { data: rawMaterialsData } = useQuery({
    queryKey: ['rawMaterialsStock', plantId],
    queryFn: () => getAllRawMaterials(plantId, { pageSize: 1000 }),
    enabled: !!plantId,
  });

  const stockMap = useMemo(() => {
    const map = {};
    if (rawMaterialsData?.items) {
      rawMaterialsData.items.forEach(rm => { map[rm.rawID] = rm.balanceQty; });
    }
    return map;
  }, [rawMaterialsData]);

  useEffect(() => {
    const fetchPreview = async () => {
      if (selectedProduct && debouncedQty > 0) {
        setIsPreviewLoading(true);
        try {
          const previewData = await getConsumptionPreview(plantId, selectedProduct, debouncedQty);
          const editableItems = previewData.map(item => {
            const rawMaterial = rawMaterialsData?.items?.find(rm => rm.rawID === item.rawMaterialID);
            return {
              ...item,
              actualQty: item.expectedQty,
              wastageQty: item.wastageQty || 0,
              mFactor: rawMaterial?.mFactor || item.mFactor || 1 
            };
          });
          setPreviewItems(editableItems);
        } catch (error) {
          setPreviewItems([]);
          if (error.message.includes('No recipe')) {
             message.warning("No recipe (mapping) found for this product.");
          }
        } finally {
          setIsPreviewLoading(false);
        }
      } else {
        setPreviewItems([]);
      }
    };
    fetchPreview();
  }, [selectedProduct, debouncedQty, plantId, rawMaterialsData]);

  const { data: historyData, isLoading: isHistoryLoading } = useQuery({
    queryKey: ['productions', plantId, historyParams],
    queryFn: () => getProductions(plantId, {
      page: historyParams.current,
      pageSize: historyParams.pageSize,
      from: historyParams.from ? historyParams.from.format('YYYY-MM-DD') : null,
      to: historyParams.to ? historyParams.to.format('YYYY-MM-DD') : null,
      readyProductId: historyParams.readyProductId
    }),
    enabled: !!plantId,
  });

  const createMutation = useMutation({
    mutationFn: (payload) => createProduction(plantId, payload),
    onSuccess: () => {
      message.success('Production entry saved and stock updated successfully!');
      form.resetFields();
      setPreviewItems([]);
      setShowEntryForm(false);
      queryClient.invalidateQueries(['productions']);
      queryClient.invalidateQueries(['rawMaterialsStock']);
    },
    onError: (err) => message.error(err.message || 'Error saving production')
  });

  const deleteMutation = useMutation({
    mutationFn: (pid) => deleteProduction(plantId, pid),
    onSuccess: () => {
      message.success('Production deleted and stock reversed successfully!');
      queryClient.invalidateQueries(['productions']);
      queryClient.invalidateQueries(['rawMaterialsStock']);
    },
    onError: (err) => message.error(err.message || 'Error deleting production')
  });

  const handleUpdateItem = (rawId, field, value) => {
    const updatedItems = previewItems.map(item => {
      if (item.rawMaterialID === rawId) return { ...item, [field]: value };
      return item;
    });
    setPreviewItems(updatedItems);
  };

  const handleSaveProduction = async () => {
    try {
      const values = await form.validateFields();
      const hasInsufficientStock = previewItems.some(item => {
        const purchaseStock = stockMap[item.rawMaterialID] || 0;
        const consumptionStock = purchaseStock * (item.mFactor || 1);
        return item.actualQty > consumptionStock;
      });

      const payload = {
        readyProductID: values.readyProductId,
        productionQty: values.productionQty,
        productionDate: values.productionDate.format('YYYY-MM-DD'),
        batchNo: values.batchNo,
        remarks: values.remarks,
        consumptionDetails: previewItems.map(item => ({
          rawMaterialID: item.rawMaterialID,
          consumptionQty: item.actualQty,
          wastageQty: item.wastageQty,
          unitID: item.unitID
        }))
      };

      if (hasInsufficientStock) {
        Modal.confirm({
          title: 'Insufficient Stock Warning',
          icon: <ExclamationCircleOutlined style={{ color: 'orange' }}/>,
          content: 'One or more raw materials have insufficient stock for this production. Do you want to proceed and allow negative stock?',
          okText: 'Yes, Proceed',
          cancelText: 'Cancel',
          onOk: () => createMutation.mutate(payload)
        });
      } else {
        createMutation.mutate(payload);
      }
    } catch (error) { console.log('Validation Failed:', error); }  
  };

  const previewColumns = [
    { title: '#', render: (t, r, index) => index + 1, width: 40 },
    { title: 'Raw Material', dataIndex: 'rawName', key: 'rawName' },
    { title: 'Unit', dataIndex: 'unitDisplayName', key: 'unitDisplayName', width: 80 },
    { title: 'Expected', dataIndex: 'expectedQty', key: 'expectedQty', render: val => <span style={{ color: 'var(--text-3)' }}>{formatQty(val)}</span> },
    { 
      title: 'Actual Qty', 
      key: 'actualQty',
      render: (_, record) => (
        <InputNumber 
          size="small"
          min={0} step="any" 
          value={record.actualQty} 
          onChange={(val) => handleUpdateItem(record.rawMaterialID, 'actualQty', val || 0)} 
        />
      )
    },
    { 
      title: 'Wastage', 
      key: 'wastageQty',
      render: (_, record) => (
        <InputNumber 
          size="small"
          min={0} step="any" 
          value={record.wastageQty} 
          onChange={(val) => handleUpdateItem(record.rawMaterialID, 'wastageQty', val || 0)} 
        />
      )
    },
    { 
      title: 'Stock', 
      key: 'currentStock', 
      render: (_, record) => {
        const purchaseStock = stockMap[record.rawMaterialID] || 0;
        const consumptionStock = purchaseStock * (record.mFactor || 1);
        const isLow = record.actualQty > consumptionStock;
        return (
          <Space size="small">
            <span style={{ color: isLow ? '#ef4444' : 'var(--text-2)', fontWeight: isLow ? 600 : 400 }}>{formatQty(consumptionStock)}</span>
            <StockBadge qty={consumptionStock} threshold={10} />
            {isLow && <Tooltip title="Insufficient Stock"><ExclamationCircleOutlined style={{ color: '#ef4444', fontSize: 12 }} /></Tooltip>}
          </Space>
        );
      }
    }
  ];

  const historyColumns = [
    { title: 'Date', dataIndex: 'productionDate', key: 'productionDate', render: val => formatDate(val) },
    { title: 'Batch No', dataIndex: 'batchNo', key: 'batchNo', render: val => val || '-' },
    { title: 'Product', dataIndex: 'productName', key: 'productName' },
    { title: 'Qty Produced', dataIndex: 'productionQty', key: 'productionQty', render: val => <span style={{ fontWeight: 600 }}>{formatQty(val)}</span> },
    { title: 'Entry By', dataIndex: 'entryBy', key: 'entryBy' },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      render: (_, record) => (
        <Space size="small">
          {canDelete && (
            <ConfirmDelete 
              itemName="Production & Reverse Stock" 
              onConfirm={() => deleteMutation.mutate(record.pid)} 
            />
          )}
        </Space>
      )
    }
  ];

  const rowClassName = (record) => {
    const purchaseStock = stockMap[record.rawMaterialID] || 0;
    const consumptionStock = purchaseStock * (record.mFactor || 1);
    return record.actualQty > consumptionStock ? 'error-row-highlight' : '';
  };

  return (
    <ConfigProvider theme={{ algorithm: theme.defaultAlgorithm }}>
      <LightThemeStyles />
      {/* यहाँ हमने ref={formContainerRef} लगा दिया है और पुराना onKeyDown हटा दिया है। 
        अब केवल इस <div> के अंदर के इनपुट्स पर ही एंटर काम करेगा।
      */}
      <div className="light-production-page" style={{ padding: '8px 16px' }} ref={formContainerRef}>
        
        <Card
          title={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 16, fontWeight: 700, textTransform: 'uppercase' }}>Production Entry</span>
              {canAdd && !showEntryForm && (
                <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setShowEntryForm(true)} style={{ background: '#0f172a' }}>
                  New Entry
                </Button>
              )}
            </div>
          }
          extra={showEntryForm && <Button onClick={() => setShowEntryForm(false)} danger size="small">Cancel</Button>}
          styles={{ header: { padding: '8px 16px', minHeight: 40 }, body: { padding: '12px 16px' } }}
        >
          
          {/* ENTRY FORM */}
          {canAdd && showEntryForm && (
            <div style={{ marginBottom: 24 }}>
              
              {/* COMPACT SECTION 1: HEADER */}
              <div className="compact-section">
                <div className="section-title">1. Production Header</div>
                <Form form={form} layout="vertical" initialValues={{ productionDate: dayjs() }}>
                  <Row gutter={12} align="bottom">
                    <Col xs={24} md={8}>
                      <Form.Item label="Ready Product" name="readyProductId" rules={[{ required: true, message: 'Req' }]}> 
                        <SearchableSelect
                          size="small"
                          apiUrl={`/plants/${plantId}/ready-products`}
                          valueField="rpid" labelField="productName"
                          placeholder="Select product..."
                          onSelect={val => {
                            form.setFieldsValue({ readyProductId: val?.rpid });
                            setPreviewItems([]);
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={12} md={4}>
                      <Form.Item label="Prod. Qty" name="productionQty" rules={[{ required: true, message: 'Req' }]}>
                        <InputNumber size="small" min={0.0001} style={{ width: '100%' }} placeholder="0" />
                      </Form.Item>
                    </Col>
                    <Col xs={12} md={4}>
                      <Form.Item label="Date" name="productionDate" rules={[{ required: true, message: 'Req' }]}>
                        <DatePicker size="small" style={{ width: '100%' }} format="DD/MM/YYYY" />
                      </Form.Item>
                    </Col>
                    <Col xs={12} md={4}>
                      <Form.Item label="Batch No." name="batchNo">
                        <Input size="small" placeholder="Optional" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={4}>
                      <Form.Item label="Remarks" name="remarks">
                        <Input size="small" placeholder="Optional notes" />
                      </Form.Item>
                    </Col>
                  </Row>
                </Form>
              </div>

              {/* COMPACT SECTION 2: RAW MATERIAL CONSUMPTION */}
              {selectedProduct && debouncedQty > 0 ? (
                <>
                  <div className="section-title" style={{ marginTop: 16 }}>2. Raw Material Consumption</div>
                  <div style={S.tableWrap}>
                    <Table 
                      columns={previewColumns} 
                      dataSource={previewItems} 
                      pagination={false} 
                      rowKey="rawMaterialID"
                      size="small"
                      loading={isPreviewLoading}
                      rowClassName={rowClassName}
                      locale={{ emptyText: "No recipe mapping found." }}
                      scroll={{ x: 'max-content' }}
                    />
                  </div>

                  {/* ACTION BAR */}
                  <div style={{ background: '#f8fafc', padding: '12px 16px', borderRadius: 4, border: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end' }}>
                    {/* <-- यहाँ Button में 'className="submit-btn"' लगाया गया है ताकि Enter दबाने पर यही क्लिक हो --> */}
                    <Button 
                      type="primary" 
                      size="middle" 
                      className="submit-btn" 
                      icon={<ToolOutlined />} 
                      onClick={handleSaveProduction}
                      loading={createMutation.isPending}
                      disabled={previewItems.length === 0}
                      style={{ background: 'var(--accent)', color: '#000', fontWeight: 600, padding: '0 24px' }}
                    >
                      Save Production
                    </Button>
                  </div>
                </>
              ) : (
                <div style={S.emptyState}>
                  <ToolOutlined style={{ fontSize: 24, color: '#cbd5e1' }} />
                  <div style={S.emptyText}>Select a Ready Product and enter Qty to load raw materials.</div>
                </div>
              )}
            </div>
          )}

          {!showEntryForm && <Divider style={{ margin: '8px 0 20px 0', borderColor: '#e2e8f0' }} />}

          {/* HISTORY SECTION */}
          <div className="section-title"><FileTextOutlined /> Recent Productions</div>
          <div style={{ marginBottom: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ width: 220 }}>
              <DatePicker.RangePicker 
                size="small" style={{ width: '100%' }}
                onChange={(dates) => setHistoryParams(prev => ({ ...prev, current: 1, from: dates ? dates[0] : null, to: dates ? dates[1] : null }))} 
              />
            </div>
            <div style={{ width: 220 }}>
              <SearchableSelect
                size="small"
                apiUrl={`/plants/${plantId}/ready-products`}
                valueField="rpid" labelField="productName"
                placeholder="Filter by Product"
                onSelect={(v) => setHistoryParams(prev => ({ ...prev, current: 1, readyProductId: v ? v.rpid : null }))}
              />
            </div>
          </div>

          <div style={S.tableWrap}>
            <DataTable 
              columns={historyColumns} 
              data={historyData?.items || []} 
              loading={isHistoryLoading} 
              rowKey="pid"
              pagination={{ current: historyParams.current, pageSize: historyParams.pageSize, total: historyData?.total || 0, size: 'small' }}
              onChange={(pagination) => setHistoryParams(prev => ({ ...prev, current: pagination.current, pageSize: pagination.pageSize }))}
              expandable={{ expandedRowRender: (record) => <ExpandedProductionDetails plantId={plantId} pid={record.pid} /> }}
            />
          </div>

        </Card>
      </div>
    </ConfigProvider>
  );
};

export default ProductionEntryPage;